import {
    EventCardActions,
    EventCardBadge,
    EventCardCollectionMembership,
    EventCardDate,
    EventCardDetails,
    EventCardImage,
    EventCardLink,
    EventCardLocation,
    EventCardOrganizer,
    EventCardPrice,
    EventCardPromoted,
    EventCardSaveAction,
    EventCardShareAction,
    EventCardTitle,
    EventCardTracking,
    EventCardUrgencySignals,
    getEventCardLinkProps,
    getShareActionProps,
    HorizontalEventCard,
    HorizontalEventCardColumn,
    HORIZONTAL_EVENT_CARD_IMAGE_CONFIG,
    useFormattedLocationString,
    useFormattedSalesStatus,
    useGetFormattedEventDateTimeString,
    useMinPriceString,
} from '@eventbrite/event-renderer';
import { gettext } from '@eventbrite/i18n';
import React from 'react';
import { DiscoverEventCardProps } from '../../types';
import './DiscoverHorizontalEventCard.scss';

export const DiscoverHorizontalEventCard: React.FunctionComponent<
    DiscoverEventCardProps
> = (props) => {
    const {
        event,
        locale,
        isRaised,
        statsigLocationString,
        affCode = 'ebdssbdestsearch',
        interfaceId,
        moreActions,
    } = props;
    const { urgencySignals } = event;

    const linkProps = getEventCardLinkProps({
        affCode: affCode,
        interfaceId,
        url: event?.url,
        isTargetBlank: true,
        isNoFollow: event.ticketsBy !== 'Eventbrite',
        onClick: () => props.onClick?.(),
        label: gettext('View %(eventName)s', { eventName: event.name }),
    });

    const formattedDateString = useGetFormattedEventDateTimeString({
        startDate: event?.startDate,
        startTime: event?.startTime,
        timezone: event?.timezone,
        repeatingInstanceCount: event?.repeatingInstanceCount,
        locale: locale,
        useUserTimezone: event.isOnlineEvent,
    });

    const formattedPriceString = useMinPriceString({
        price: event?.minPrice,
        locale: locale,
    });

    const formattedLocationString = useFormattedLocationString({
        venue: event.venue,
        style: 'full',
    });

    //Only show active Sales Status (sold out etc)
    //For non-repeating events to prevent incorrect signals.
    const formattedSalesStatus = useFormattedSalesStatus(
        event.eventSalesStatus,
        (event?.repeatingInstanceCount || 0) > 0,
    );

    const hasCollections =
        (event?.publicCollections?.creatorCollections?.objectCount || 0) > 0;

    const shouldShowUrgencySignals = !!urgencySignals && !formattedSalesStatus;

    const eventCardTrackingProps = {
        statsigLocationString: statsigLocationString,
        eventData: {
            isPromoted: !!event.isPromoted,
            isFree: event.isFree,
            isOnlineEvent: event.isOnlineEvent,
            hasUrgencySignals: shouldShowUrgencySignals,
            id: event.id,
            venue: event.venue,
            tags: event.tags,
        },
    };

    return (
        <section className="discover-horizontal-event-card">
            <HorizontalEventCard isRaised={isRaised}>
                <EventCardTracking {...eventCardTrackingProps}>
                    <EventCardBadge
                        style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                        }}
                    >
                        {formattedSalesStatus}
                    </EventCardBadge>
                    <EventCardDetails
                        tokens={{ '--EventCardDetailsPadding': '0 16px 0 0' }}
                    >
                        <EventCardPromoted
                            isPromoted={event.isPromoted}
                            tokens={{ '--PromotedPaddingBottom': '4px' }}
                        />
                        <EventCardLink {...linkProps}>
                            <EventCardTitle
                                className="event-card__clamp-line--three"
                                title={event.name}
                            />
                        </EventCardLink>
                        <EventCardDate
                            className="eds-text-color--primary-brand"
                            formattedDateString={formattedDateString}
                            variant="body-md-bold"
                        />
                        <EventCardLocation
                            className="event-card__clamp-line--one"
                            formattedLocationString={formattedLocationString}
                        />
                        <EventCardPrice
                            isFree={event.isFree}
                            formattedPriceString={formattedPriceString}
                        />
                        {hasCollections ? (
                            <EventCardCollectionMembership
                                style={{ paddingTop: '12px' }}
                                eventId={event.id}
                                collections={event.publicCollections}
                            />
                        ) : (
                            <EventCardOrganizer
                                organizerName={event.organizerName}
                                organizerFollowerCount={
                                    event.organizerFollowerCount
                                }
                                variantStyle="body-md-bold"
                            />
                        )}
                    </EventCardDetails>
                    <HorizontalEventCardColumn>
                        {shouldShowUrgencySignals && (
                            <EventCardUrgencySignals
                                className="discover-horizontal-event-card__urgency-signals"
                                urgencySignals={urgencySignals}
                            />
                        )}
                        <EventCardLink {...linkProps}>
                            <EventCardImage
                                name={event.name}
                                loading={props.isLazyImage ? 'lazy' : 'eager'}
                                tokens={{
                                    ...HORIZONTAL_EVENT_CARD_IMAGE_CONFIG,
                                    '--image-background-color': event.edgeColor,
                                }}
                                src={event.imageUrl}
                            />
                        </EventCardLink>
                        <EventCardActions
                            tokens={{
                                '--EventCardActionsMargin': '12px 0 0 0',
                            }}
                        >
                            <EventCardShareAction
                                {...getShareActionProps({
                                    name: event.name,
                                    id: event.id,
                                    url: event.url,
                                    affCode: affCode,
                                })}
                            />
                            <EventCardSaveAction
                                isAuthenticated={!!props.isAuthenticated}
                                savedByYou={event.savedByYou}
                                id={event.id}
                                eventName={event.name}
                                imageUrl={event.imageUrl}
                            />
                            {moreActions}
                        </EventCardActions>
                    </HorizontalEventCardColumn>
                </EventCardTracking>
            </HorizontalEventCard>
        </section>
    );
};
